import { Theme, Typography } from '@mui/material';
import { FC } from 'react';
import { FaMinus } from 'react-icons/fa';
import { makeStyles } from 'tss-react/mui';
import DateFormat from '../../../elements/Date/DateFormat';
import ExternalLinkButton from '../../../elements/ExternalLinkButton/ExternalLinkButton';
import OwnerCell from '../../../elements/TableCells/OwnerCell';
import {
  ArtifactInfo,
  ArtifactRegistryDescription,
  RegistryTypes,
} from '../../types/artifacts-type';
import { getRegistryIconType } from '../../utils/artifacts-utils';
import ArtifactSections from './ArtifactSections';
import InfoIconSection from './InfoIconSection';
import Section from './Section';

const ArtifactRegistryInfo: FC<ArtifactRegistryInfoProps> = ({
  artifact,
  registry,
}) => {
  const { classes } = useStyles();
  const RegistryIcon = getRegistryIconType(registry.type);
  const registryType = registry.type;
  const name = registry.name;
  const project = registry.project;
  const hash = artifact.artifactInfo.hash || '';
  const tags = registry.tags;
  const username = registry.username;
  const uploadingUserType = registry.userType;
  const uploadDate = new Date(registry.uploadTime);
  const lastUpdate = new Date(registry.lastUpdate);
  const buildTime = new Date(registry.buildTime);
  const link = registry.link;

  return (
    <div className={classes.artifactRegistryInfo}>
      <ArtifactSections withMargin={false}>
        <Section label='Registry Type' style={{ minWidth: 300 }}>
          <Typography
            variant='body1'
            color='text.primary'
            className={classes.registryType}>
            {RegistryIcon && (
              <RegistryIcon
                height='1rem'
                color={registryType === RegistryTypes.JFrog ? '#41BF47' : ''}
              />
            )}
            {registryType}
            <ExternalLinkButton size='small' href={link} target='_blank' />
          </Typography>
        </Section>

        <Section label='Name'>{name}</Section>
        <Section label='Project'>{project}</Section>
        <Section label='Hash'>{hash}</Section>
        <Section label='Tags'>{tags && tags.join(', ')}</Section>
        <InfoIconSection
          label='Username'
          labelIcon='The name of the user who uploaded the artifact, like a pipeline dedicated user or a developers name'>
          {username && username.length > 0 ? (
            <OwnerCell owners={[username]} />
          ) : (
            <FaMinus />
          )}
        </InfoIconSection>

        <Section label='Uploading User Type'>{uploadingUserType}</Section>
        <InfoIconSection
          label='Time Upload'
          labelIcon='When was the artifact uploaded to the registry'>
          <DateFormat date={uploadDate} color='text.primary' bigSize />
        </InfoIconSection>

        <InfoIconSection
          label='Last Update'
          labelIcon='if the artifact was overwritten on the registry server, and if so, when Build Time: when was the artifact built'>
          <DateFormat date={lastUpdate} color='text.primary' bigSize />
        </InfoIconSection>

        <Section label='Build Time'>
          <DateFormat date={buildTime} color='text.primary' bigSize />
        </Section>
      </ArtifactSections>
    </div>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  artifactRegistryInfo: {
    padding: theme.spacing(2),
    background: theme.palette.background.default,
    marginBlock: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  registryType: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

export interface ArtifactRegistryInfoProps {
  artifact: ArtifactInfo;
  registry: ArtifactRegistryDescription;
}

export default ArtifactRegistryInfo;
